// client/src/components/pages/LexiStar/TripDetail/TripDetail.tsx
//
// LexiStar trip detail (ST-00188, M2; LS-09 §5.5). Route /lexistar/trips/:id,
// page permission lexistar.trips.view. Data: GET /api/lexistar/trips/:id
// (trip_view row with legs, timeline, deviations, ticket matches, fuel —
// m2-contracts §4.2; the actual `track` is a UI assumption, see the service).
//
// Sections as the LS-09 wireframe: header (reference, stage, combination,
// driver, route and load); map (planned vs actual, approved detours dashed,
// unapproved solid); weights (dispatch / receipt / variance with presence
// badges); distance & time; fuel (original → adjusted allowance, used with
// provenance, per-leg over-consumption); exceptions; timeline. Every figure
// has a provenance marker or a "how was this calculated?" expander.
//
// Actions: Dispatch (PLANNED, lexistar.trips.dispatch), Cancel / Abort with a
// reason (lexistar.trips.manage). Live: the trip room (`trip:{id}`) refetches
// on events.
//
// M3-U1 (m3-contracts §9; LS-04 §4.1; T4-S13, T4-S18): the record-state chip
// (OPEN / SIGNED_OFF / AMENDED) beside the stage; an AMENDED watermark banner;
// the Record panel — sign-off (lexistar.trips.signoff, only when COMPLETED or
// RECONCILED with no open review items, the blocking items listed), amendment
// request and second-user approve / reject (lexistar.trips.amend), audit trail;
// Re-plan for an active trip (lexistar.trips.dispatch, POST /trips/:id:replan);
// Replay (→ /lexistar/replay?trip_id=, M3-U2's page).
//
// M4-R2 (m4-contracts §7): [Report] opens the trip report
// (/lexistar/reports/trip/:id) and [PDF] runs `trip_report` and downloads the
// renderer's PDF — both with lexistar.reports.view. Converted to TypeScript in
// the same change (CLAUDE.md rule); behaviour otherwise unchanged.
//
// ST-00207 (m4b-contracts §5, phone pass): under 640 px or on a touch screen the page is a
// phone layout — the header keeps the title, stage, record chip and Refresh; the truck, driver
// and route open the first card with Report / PDF / Share / Board under it; exceptions come
// first (LS-09 §1), then the map as a 260 px panel with a full-screen toggle, the figures, the
// record and a timeline that reads top to bottom (who did each step, since a phone cannot
// hover). The trip actions (Dispatch, Re-plan…, Replay, Cancel trip, Abort trip) sit in a sticky
// bottom bar; the dialogs take 44 px targets. A chat link to /lexistar/trips/<id> lands on the
// trip with the bar showing. The desktop layout is unchanged.

import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FaRoute,
  FaSyncAlt,
  FaPaperPlane,
  FaBan,
  FaStopCircle,
  FaLink,
  FaArrowLeft,
  FaTimes,
  FaDirections,
  FaPlay,
  FaFileAlt,
  FaShareAlt,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { useAuth } from '../../../../context/AuthContext';
import {
  getTrip,
  dispatchTrip,
  cancelTrip,
  abortTrip,
  pickList,
} from '../../../../services/lexistarStatusBoard';
import useLexiStarEvents from '../StatusBoard/useLexiStarEvents';
import {
  PageHeader as PageHeaderJs,
  ErrorBanner,
  NoticeBanner,
  LoadingState,
  NoPermissionPage,
  ConnectionIndicator,
  StagePill,
  ProvenanceMarker,
  OverConsumptionTag,
  HowCalculated,
  inputClasses,
  cardClasses,
} from '../StatusBoard/atoms';
import {
  fmtNum,
  fmtKm,
  fmtL,
  fmtKg,
  fmtSignedL,
  fmtRand,
  fmtDuration,
  fmtTime,
  fmtDateTime,
} from '../StatusBoard/format';
import { isAboveRouteNorm } from '../StatusBoard/fuelLogic';
import { ACTIVE_STAGES, TONES } from '../StatusBoard/statusLanguage';
import CorridorMapJs from '../Approvals/CorridorMap';
import useRouteGeometries from '../Approvals/useRouteGeometries';
import { RecordStateChip } from '../ReviewQueue/assuranceAtoms';
import RecordPanel from './RecordPanel';
import ReplanDialog from './ReplanDialog';
import {
  weighVariance,
  approvedExtraKm,
  spanSeconds,
  timelineLabel,
  VARIANCE_TOL_PCT,
  VARIANCE_TOL_KG,
} from './tripMath';
// M4-R2: the trip report's PDF export (runs `trip_report`, waits, downloads).
import { ExportButtons } from '../Reports/reportAtoms';
// ST-00207: the phone layout.
import { usePhoneLayout } from '../../../common/phoneLayout';
import PhoneActionBar, { PhoneAction, phoneActionClass } from '../../../common/PhoneActionBar';
import PhoneMapPanel, { PHONE_MAP_CANVAS } from '../../../common/PhoneMapPanel';

// ── Shapes (m2/m3 contracts; the server may send more) ──────────────────────

type Num = number | string | null | undefined;

interface OverConsumption {
  excess_l: number;
  severity: string;
  certainty: string;
}

interface Leg {
  id?: string;
  seq?: number;
  kind?: string;
  load_state?: string;
  from_name?: string | null;
  to_name?: string | null;
  route_id?: string | null;
  route_version?: number | null;
  route_code?: string | null;
  planned_km?: Num;
  budget_l?: Num;
  consumed_l?: Num;
  consumed_provenance?: string | null;
  over_consumption?: OverConsumption | null;
  started_at?: string | null;
  ended_at?: string | null;
}

interface Trip {
  id: string;
  reference?: string;
  stage?: string;
  record_state?: string | null;
  watermark?: string | null;
  amended_at?: string | null;
  registration?: string | null;
  asset_key?: string | null;
  combination?: { horse?: string | null; trailers?: string[] } | null;
  driver_name?: string | null;
  driver_tag?: string | null;
  load?: {
    material?: string | null;
    order_ref?: string | null;
    consignor?: string | null;
    consignee?: string | null;
    planned_tonnes?: Num;
  } | null;
  planned_start?: string | null;
  run_id?: string | null;
  seq?: number | null;
  origin?: string | null;
  eta?: string | null;
  dispatch_net_kg?: Num;
  receipt_net_kg?: Num;
  planned_km?: Num;
  adjusted_km?: Num;
  actual_km?: Num;
  distance_provenance?: string | null;
  route_avg_km?: Num;
  dispatched_at?: string | null;
  completed_at?: string | null;
  budget_original_l?: Num;
  budget_adjusted_l?: Num;
  consumed_l?: Num;
  consumed_provenance?: string | null;
  pinned?: Pinned | null;
  variance_breach?: boolean | null;
  status_flags?: string[] | null;
  legs?: Leg[];
}

interface Pinned {
  fuel_price_per_l?: number | null;
  norms?: Record<string, { l_per_100km?: number | null } | undefined>;
}

interface Match {
  id: string;
  ticket_type?: string;
  state?: string;
  presence?: string;
  presence_confidence?: number | null;
  weighed_at?: string | null;
  net_kg?: Num;
  ticket?: { weighed_at?: string | null; net_kg?: Num } | null;
}

interface Deviation {
  id: string;
  state?: string;
  opened_at?: string | null;
  closed_at?: string | null;
  off_route_km?: Num;
  reason_code?: string | null;
  track?: unknown;
}

interface TimelineEntry {
  id?: string | number;
  at: string;
  kind?: string;
  actor?: string | null;
  /** When the entry was written (ST-00205: an engine step about a silent truck lags `at`). */
  recorded_at?: string | null;
}

/** An entry recorded this much after its own time shows when it was recorded (ST-00205). */
const RECORDED_LATE_MS = 5 * 60 * 1000;

/**
 * ST-00205: when an entry was recorded well after the moment it is about — the engine applies a
 * dispatch to a truck whose tracker was asleep only when it next reports — else null.
 */
export function recordedLateAt(t: Pick<TimelineEntry, 'at' | 'recorded_at'>): string | null {
  if (!t.recorded_at) return null;
  const at = new Date(t.at).getTime();
  const rec = new Date(t.recorded_at).getTime();
  return Number.isFinite(at) && Number.isFinite(rec) && rec - at > RECORDED_LATE_MS ? t.recorded_at : null;
}

interface FuelData {
  budget_original_l?: Num;
  budget_adjusted_l?: Num;
  consumed_l?: Num;
  provenance?: string | null;
  consumed_provenance?: string | null;
  variance_l?: Num;
  variance_r?: Num;
  pinned?: Pinned | null;
  norms?: { loaded_l_per_100km?: Num; empty_l_per_100km?: Num } | null;
  observed?: { loaded_l_per_100km?: Num; empty_l_per_100km?: Num } | null;
  over_consumption?: OverConsumption | null;
}

interface TripBody {
  trip?: Trip;
  legs?: Leg[];
  track?: unknown;
  fuel?: FuelData | null;
  amendments?: unknown;
  /** The caller may command the trip (its organisation planned it, or a global admin). */
  can_change?: boolean;
  /** Read from the caller's projection: another organisation's trip it dispatches / receives (ST-00233). */
  partner?: boolean;
  [k: string]: unknown;
}

// ── Typed views of the JS pieces this page uses ─────────────────────────────

interface AuthView {
  hasPermission: (key: string) => boolean;
}

const PageHeader = PageHeaderJs as unknown as ComponentType<{
  icon?: IconType;
  title: string;
  subtitle?: ReactNode;
  toolbar?: ReactNode;
  actions?: ReactNode;
}>;

const CorridorMap = CorridorMapJs as unknown as ComponentType<{
  idPrefix?: string;
  routes?: Array<{ key: string; feature: unknown }>;
  track?: unknown;
  deviations?: Array<{ id: string; geometry: unknown; approved: boolean }>;
  positionColor?: string;
  className?: string;
}>;

interface Variance {
  kg: number;
  pct: number | null;
  tolerance: number;
  breach: boolean;
}

const errMessage = (err: unknown, fallback: string): string => {
  const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string } | null;
  return e?.response?.data?.error || e?.message || fallback;
};

const LIVE_REFETCH_DEBOUNCE_MS = 1500;
const CANCELLABLE = ['PLANNED', 'DISPATCHED', 'ACKNOWLEDGED'];
const ABORTABLE = ['EN_ROUTE_TO_SOURCE', 'AT_SOURCE', 'IN_TRANSIT_LOADED'];

const PRESENCE: Record<string, { label: string; glyph: string; className: string }> = {
  present: { label: 'presence verified', glyph: '✔', className: 'bg-emerald-50 text-emerald-700' },
  absent: {
    label: 'no presence at the weighbridge',
    glyph: '✖',
    className: 'bg-red-50 text-red-700',
  },
  unknown: { label: 'presence unknown', glyph: '?', className: 'bg-slate-100 text-slate-600' },
};

export function PresenceBadge({
  presence,
  confidence,
}: {
  presence?: string | null;
  confidence?: number | null;
}) {
  const meta = PRESENCE[presence || 'unknown'] || PRESENCE.unknown!;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold ${meta.className}`}
      title={confidence != null ? `${meta.label} (${confidence}% confidence)` : meta.label}
    >
      <span aria-hidden="true">{meta.glyph}</span>
      {meta.label}
    </span>
  );
}

const Section = ({
  title,
  children,
  className = '',
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) => (
  <section className={`${cardClasses} p-5 ${className}`} aria-label={title}>
    <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{title}</h2>
    {children}
  </section>
);

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex items-baseline justify-between gap-4 py-1">
    <dt className="text-sm text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-900 text-right tabular-nums">{children}</dd>
  </div>
);

function ReasonDialog({
  open,
  title,
  confirmLabel,
  onCancel,
  onConfirm,
  busy,
  error,
  phone = false,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  busy: boolean;
  error: string | null;
  /** ST-00207: a bottom sheet with 44 px targets and a 16 px field (no zoom on focus). */
  phone?: boolean;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  if (!open) return null;
  return (
    <div
      className={
        phone
          ? 'phone-targets fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm'
          : 'fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm'
      }
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={
          phone
            ? 'bg-surface rounded-t-2xl shadow-2xl w-full max-w-lg overflow-hidden pb-[env(safe-area-inset-bottom)]'
            : 'bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden'
        }
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button
            onClick={onCancel}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="p-6 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          <label
            htmlFor="lexistar-trip-reason"
            className="block text-sm font-medium text-slate-700"
          >
            Reason
          </label>
          <input
            id="lexistar-trip-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            className={inputClasses}
            placeholder="Why? (recorded on the trip timeline)"
          />
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Keep trip
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || !reason.trim()}
            className={`px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm ${busy || !reason.trim() ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'}`}
          >
            {busy ? 'Saving...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Weights({ trip, matches }: { trip: Trip; matches: Match[] }) {
  const byType = (t: string) => matches.find((m) => m.ticket_type === t && m.state !== 'rejected');
  const dispatch = byType('dispatch');
  const receipt = byType('receipt');
  const v = weighVariance(trip) as Variance | null;
  const line = (label: string, m: Match | undefined, net: Num) => (
    <Row label={label}>
      {net != null ? (
        <span className="inline-flex flex-wrap items-center justify-end gap-2">
          <span className="font-semibold">{fmtKg(net)}</span>
          {(m?.ticket?.weighed_at || m?.weighed_at) && (
            <span className="text-slate-500">{fmtTime(m.ticket?.weighed_at || m.weighed_at)}</span>
          )}
          {m && <PresenceBadge presence={m.presence} confidence={m.presence_confidence} />}
        </span>
      ) : (
        <span className="text-slate-400">no ticket matched yet</span>
      )}
    </Row>
  );
  return (
    <Section title="Weights">
      <dl>
        {line(
          'Dispatch net',
          dispatch,
          trip.dispatch_net_kg ?? dispatch?.ticket?.net_kg ?? dispatch?.net_kg
        )}
        {line(
          'Receipt net',
          receipt,
          trip.receipt_net_kg ?? receipt?.ticket?.net_kg ?? receipt?.net_kg
        )}
        <Row label="Variance">
          {v ? (
            <span className="inline-flex items-center gap-2">
              <span className={v.breach ? 'font-semibold text-red-700' : ''}>
                {v.kg > 0 ? '−' : v.kg < 0 ? '+' : ''}
                {fmtKg(Math.abs(v.kg))} (
                {v.pct != null
                  ? `${v.kg > 0 ? '−' : v.kg < 0 ? '+' : ''}${Math.abs(v.pct * 100).toFixed(2)}%`
                  : '—'}
                )
              </span>
              <span
                className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${v.breach ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'}`}
              >
                {v.breach ? 'outside tolerance' : 'within tolerance'}
              </span>
            </span>
          ) : (
            '—'
          )}
        </Row>
      </dl>
      {v && (
        <HowCalculated>
          <div>
            variance = dispatch net − receipt net = {fmtNum(trip.dispatch_net_kg)} −{' '}
            {fmtNum(trip.receipt_net_kg)} = {fmtNum(v.kg)} kg
          </div>
          <div>
            shown as the receipt against dispatch: {v.kg > 0 ? '−' : '+'}
            {fmtNum(Math.abs(v.kg))} kg
          </div>
          <div>
            tolerance = max({(VARIANCE_TOL_PCT * 100).toFixed(1)}% × {fmtNum(trip.dispatch_net_kg)},{' '}
            {VARIANCE_TOL_KG} kg) = {fmtNum(v.tolerance)} kg
          </div>
          <div>
            breach = |{fmtNum(v.kg)}| &gt; {fmtNum(v.tolerance)} → {v.breach ? 'yes' : 'no'}
          </div>
        </HowCalculated>
      )}
    </Section>
  );
}

function DistanceTime({ trip, legs }: { trip: Trip; legs: Leg[] }) {
  const extra = approvedExtraKm(trip) as number | null;
  const loadedLeg = legs.find((l) => l.kind === 'loaded');
  return (
    <Section title="Distance & time">
      <dl>
        <Row label="Planned">{fmtKm(trip.planned_km)}</Row>
        <Row label="Adjusted">
          {fmtKm(trip.adjusted_km ?? trip.planned_km)}
          {extra != null && extra > 0 && (
            <span className="text-slate-500"> (+{fmtNum(extra)} approved)</span>
          )}
        </Row>
        <Row label="Actual">
          <span className="inline-flex items-center gap-1.5">
            {fmtKm(trip.actual_km)}
            <ProvenanceMarker provenance={trip.distance_provenance || 'derived'} />
            {isAboveRouteNorm(trip) && (
              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-semibold">
                above route norm
              </span>
            )}
          </span>
        </Row>
        <Row label="Route average">
          {trip.route_avg_km != null ? fmtKm(trip.route_avg_km) : '— (fewer than five trips)'}
        </Row>
        <Row label="Duration">
          {fmtDuration(spanSeconds(trip.dispatched_at, trip.completed_at))}
        </Row>
        {legs.map((l) => (
          <Row
            key={l.id || l.seq}
            label={`${l.kind === 'loaded' ? 'Loaded' : l.kind === 'positioning' ? 'Positioning' : 'Closing'} leg`}
          >
            {l.from_name} → {l.to_name} · {fmtKm(l.planned_km)} planned
            {l.started_at &&
              l.ended_at &&
              ` · ${fmtDuration(spanSeconds(l.started_at, l.ended_at))}`}
          </Row>
        ))}
      </dl>
      <HowCalculated>
        <div>
          planned = Σ leg planned km = {legs.map((l) => fmtNum(l.planned_km)).join(' + ') || '—'} ={' '}
          {fmtNum(trip.planned_km)} km
        </div>
        <div>
          adjusted = planned + approved extra km = {fmtNum(trip.planned_km)} + {fmtNum(extra ?? 0)}{' '}
          = {fmtNum(trip.adjusted_km ?? trip.planned_km)} km
        </div>
        <div>
          actual = distance travelled from dispatch to completion (odometer when measured, else GPS)
        </div>
        {loadedLeg && (
          <div>
            route {loadedLeg.route_code || loadedLeg.route_id || '(proposed)'}
            {loadedLeg.route_version != null && ` v${loadedLeg.route_version}`} is pinned for this
            trip
          </div>
        )}
        <div>above route norm = actual &gt; average × 1.05 and at least 3 km above</div>
      </HowCalculated>
    </Section>
  );
}

function Fuel({
  trip,
  legs,
  fuel,
  canSeeRand,
}: {
  trip: Trip;
  legs: Leg[];
  fuel: FuelData | null | undefined;
  canSeeRand: boolean;
}) {
  const budgetOriginal = fuel?.budget_original_l ?? trip.budget_original_l;
  const budgetAdjusted = fuel?.budget_adjusted_l ?? trip.budget_adjusted_l ?? budgetOriginal;
  const used = fuel?.consumed_l ?? trip.consumed_l;
  const provenance = fuel?.provenance ?? fuel?.consumed_provenance ?? trip.consumed_provenance;
  const variance =
    fuel?.variance_l ??
    (used != null && budgetAdjusted != null ? Number(budgetAdjusted) - Number(used) : null);
  // Norms / price pinned at dispatch (A2 exposes trip.pinned as fuel.pinned).
  const pinned: Pinned = fuel?.pinned || trip.pinned || {};
  const normOf = (state: string | undefined): Num =>
    (state === 'loaded' ? fuel?.norms?.loaded_l_per_100km : fuel?.norms?.empty_l_per_100km) ??
    pinned.norms?.[state || '']?.l_per_100km ??
    null;
  const price = pinned.fuel_price_per_l ?? null;
  const varianceR =
    fuel?.variance_r ?? (variance != null && price != null ? Number(variance) * price : null);
  return (
    <Section title="Fuel">
      <dl>
        <Row label="Allowance">
          {budgetAdjusted != null && Number(budgetAdjusted) !== Number(budgetOriginal) ? (
            <>
              {fmtL(budgetOriginal)} → <span className="font-semibold">{fmtL(budgetAdjusted)}</span>{' '}
              adjusted
            </>
          ) : (
            fmtL(budgetOriginal)
          )}
          {trip.origin === 'prompted' && <span className="text-slate-500"> · retrospective</span>}
        </Row>
        <Row label="Used">
          <span className="inline-flex items-center gap-1.5">
            {fmtL(used)}
            <ProvenanceMarker provenance={provenance} />
            <OverConsumptionTag
              fuel={{
                consumed_l: used,
                budget_adjusted_l: budgetAdjusted,
                provenance,
                over_consumption: fuel?.over_consumption,
              }}
            />
          </span>
        </Row>
        {fuel?.observed && (
          <Row label="Observed">
            Loaded {fmtNum(fuel.observed.loaded_l_per_100km, 1)} · Empty{' '}
            {fmtNum(fuel.observed.empty_l_per_100km, 1)} L/100 km
          </Row>
        )}
        <Row label="Variance vs adjusted">
          {fmtSignedL(variance)}
          {canSeeRand && varianceR != null && (
            <span className="text-slate-500"> ({fmtRand(varianceR)})</span>
          )}
        </Row>
        {legs.map((l) => (
          <Row
            key={`fuel-${l.id || l.seq}`}
            label={`${l.load_state === 'loaded' ? 'Loaded' : 'Empty'} leg`}
          >
            <span className="inline-flex items-center gap-1.5">
              {fmtL(l.consumed_l)}
              <ProvenanceMarker provenance={l.consumed_provenance} />
              <span className="text-slate-500">/ {fmtL(l.budget_l)}</span>
              <OverConsumptionTag
                fuel={{
                  consumed_l: l.consumed_l,
                  budget_l: l.budget_l,
                  provenance: l.consumed_provenance,
                  over_consumption: l.over_consumption,
                }}
              />
            </span>
          </Row>
        ))}
      </dl>
      <HowCalculated>
        <div>
          allowance = Σ leg km × norm ÷ 100 (+ idle allowance, contingency) — pinned at dispatch
        </div>
        {legs.map((l) => (
          <div key={`calc-${l.id || l.seq}`}>
            {l.load_state} leg: {fmtNum(l.planned_km)} km → {fmtL(l.budget_l)}
            {normOf(l.load_state) != null && ` (norm ${fmtNum(normOf(l.load_state), 1)} L/100 km)`}
          </div>
        ))}
        <div>
          adjusted = original + approved extra km × norm ÷ 100 = {fmtL(budgetOriginal)} +{' '}
          {fmtNum(Number(budgetAdjusted ?? 0) - Number(budgetOriginal ?? 0))} L = {fmtL(budgetAdjusted)}
        </div>
        <div>
          variance = adjusted − used = {fmtNum(budgetAdjusted)} − {fmtNum(used)} ={' '}
          {fmtSignedL(variance)} (negative = over)
        </div>
        <div>
          over-consumption flag: used − allowance &gt; max(3%, 2 L); medium ≤ 10%, high above;
          confirmed only when measured. It informs — it never blocks.
        </div>
      </HowCalculated>
    </Section>
  );
}

function Exceptions({
  trip,
  legs,
  deviations,
  matches,
}: {
  trip: Trip;
  legs: Leg[];
  deviations: Deviation[];
  matches: Match[];
}) {
  const items: Array<{ key: string; text: string; state: string }> = [];
  deviations.forEach((d) =>
    items.push({
      key: `dev-${d.id}`,
      text: `Deviation ${fmtTime(d.opened_at)}–${d.closed_at ? fmtTime(d.closed_at) : 'open'} · ${fmtKm(d.off_route_km, 1)} off route${d.reason_code ? ` · ${d.reason_code.replace(/_/g, ' ')}` : ''}`,
      state: String(d.state),
    })
  );
  legs
    .filter((l) => l.over_consumption)
    .forEach((l) =>
      items.push({
        key: `oc-${l.seq}`,
        text: `Over-consumption on the ${l.load_state} leg: +${fmtNum(l.over_consumption!.excess_l, 1)} L (${l.over_consumption!.certainty})`,
        state: l.over_consumption!.severity,
      })
    );
  if (trip.variance_breach)
    items.push({ key: 'var', text: 'Weighbridge variance outside tolerance', state: 'breach' });
  matches
    .filter((m) => m.presence === 'absent')
    .forEach((m) =>
      items.push({
        key: `pr-${m.id}`,
        text: `${m.ticket_type} ticket without presence at the weighbridge`,
        state: 'check',
      })
    );
  (trip.status_flags || []).forEach((f) =>
    items.push({ key: `flag-${f}`, text: timelineLabel(f), state: 'flag' })
  );
  return (
    <Section title="Exceptions">
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">No exceptions on this trip.</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((i) => (
            <li
              key={i.key}
              className="flex items-start justify-between gap-3 text-sm text-slate-800"
            >
              <span>• {i.text}</span>
              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-semibold uppercase whitespace-nowrap">
                {String(i.state).replace(/_/g, ' ').toLowerCase()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** Phone: the timeline top to bottom, each step with who did it (a phone cannot hover). */
function PhoneTimeline({ timeline }: { timeline: TimelineEntry[] }) {
  return (
    <Section title="Timeline">
      {timeline.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing has happened on this trip yet.</p>
      ) : (
        <ol className="space-y-2.5 text-sm" aria-label="Trip timeline">
          {[...timeline]
            .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
            .map((t, i) => (
              <li key={t.id ?? i} className="flex items-baseline gap-3">
                <span className="w-12 flex-shrink-0 tabular-nums text-slate-500">{fmtTime(t.at)}</span>
                <span className="min-w-0 text-slate-900">
                  {timelineLabel(t.kind)}
                  {t.actor && <span className="block text-xs text-slate-500">by {t.actor}</span>}
                  {recordedLateAt(t) && (
                    <span className="block text-xs text-slate-500">
                      recorded {fmtTime(recordedLateAt(t) as string)}, when the truck next reported
                    </span>
                  )}
                </span>
              </li>
            ))}
        </ol>
      )}
    </Section>
  );
}

const phoneToolClass = phoneActionClass();

const TripDetail = () => {
  const { id } = useParams();
  const { hasPermission } = useAuth() as unknown as AuthView;
  const canView = hasPermission('lexistar.trips.view');
  const mayManage = hasPermission('lexistar.trips.manage');
  const mayDispatch = hasPermission('lexistar.trips.dispatch');
  const canSeeRand = hasPermission('lexistar.fuel.manage');
  const mayReplay = hasPermission('lexistar.replay.view');
  const canReport = hasPermission('lexistar.reports.view'); // M4-R2
  const phone = usePhoneLayout(); // ST-00207

  const [data, setData] = useState<TripBody | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'cancel' | 'abort' | 'replan' | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const load = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      if (!canView || !id) return;
      try {
        if (!quiet) setLoading(true);
        setData((await getTrip(id)) as TripBody);
        setError(null);
      } catch (err) {
        if (!quiet) {
          const e = err as { response?: { status?: number; data?: { error?: string } } };
          setError(
            e.response?.status === 404
              ? 'Trip not found, or not visible to your organisation.'
              : e.response?.data?.error || 'Failed to load the trip.'
          );
        }
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [canView, id]
  );

  useEffect(() => {
    load();
    return () => clearTimeout(refetchTimer.current);
  }, [load]);

  // ST-00233: another organisation's trip (read from the caller's projection) changes nothing
  // here and has no live room (the server refuses it; the trip's raw events are not a
  // partner's): the actions follow the server's can_change, the replay is the trip's own.
  const isPartner = data?.partner === true;
  const ownTrip = !!data && data.can_change !== false && !isPartner;
  const canManage = mayManage && ownTrip;
  const canDispatch = mayDispatch && ownTrip;
  const canReplay = mayReplay && !isPartner;

  const connection = useLexiStarEvents({
    tripId: id,
    enabled: canView && !!id && !!data && !isPartner,
    onEvent: () => {
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(() => load({ quiet: true }), LIVE_REFETCH_DEBOUNCE_MS);
    },
  });

  const trip = data?.trip;
  const legs: Leg[] = trip?.legs || data?.legs || [];
  const deviations = pickList(data, 'deviations') as Deviation[];
  const matches = pickList(data, 'ticket_matches') as Match[];
  const timeline = pickList(data, 'timeline') as TimelineEntry[];
  const { lines } = useRouteGeometries(
    legs
      .filter((l) => l.route_id)
      .map((l) => ({ route_id: l.route_id, route_version: l.route_version })) as Array<{
      route_id: string;
      route_version: number;
    }>,
    { corridor: false }
  ) as { lines: Array<{ key: string; feature: unknown }> };

  const runAction = async (fn: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      setDialog(null);
      setNotice(message);
      await load({ quiet: true });
    } catch (err) {
      const msg = errMessage(err, 'The action failed');
      if (dialog) setActionError(msg);
      else setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setNotice('Link copied.');
    } catch {
      setNotice(window.location.href);
    }
  };

  // Phone: the system share sheet (WhatsApp, e-mail …) where there is one, else copy the link.
  const shareLink = async () => {
    const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
    if (typeof nav.share !== 'function') return copyLink();
    try {
      await nav.share({ title: trip ? `Trip ${trip.reference}` : 'Trip', url: window.location.href });
    } catch {
      /* dismissed */
    }
  };

  const loadedLeg = legs.find((l) => l.kind === 'loaded');
  const combination = trip?.combination;
  const vehicle = trip
    ? [
        combination?.horse ||
          trip.registration ||
          String(trip.asset_key || '').replace(/^(reg|asset):/, ''),
        (combination?.trailers || []).join('/'),
      ]
        .filter(Boolean)
        .join(' + ')
    : '';

  const header = (
    <PageHeader
      icon={FaRoute}
      title={trip ? `Trip ${trip.reference}` : 'Trip'}
      subtitle={
        trip
          ? `${vehicle} · ${trip.driver_name || trip.driver_tag || 'No driver'} · ${loadedLeg ? `${loadedLeg.from_name} → ${loadedLeg.to_name}` : ''}${loadedLeg?.route_version != null ? ` (v${loadedLeg.route_version})` : ''}`
          : undefined
      }
      toolbar={
        trip ? (
          <div className="flex flex-wrap items-center gap-2">
            <StagePill stage={trip.stage} />
            <RecordStateChip state={trip.record_state || 'OPEN'} />
          </div>
        ) : null
      }
      actions={
        canView && phone ? (
          <div className="flex items-center gap-2">
            <ConnectionIndicator status={connection} compact />
            <button
              onClick={() => load()}
              className="touch-target inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
              aria-label="Refresh"
            >
              <FaSyncAlt size={11} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        ) : canView ? (
          <div className="flex items-center gap-2">
            <ConnectionIndicator status={connection} />
            <Link
              to="/lexistar/status-board"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <FaArrowLeft size={11} /> <span className="hidden sm:inline">Board</span>
            </Link>
            {/* M4-R2: the trip report and its PDF (LS-09 §5.5 [PDF] [Share]) */}
            {trip && canReport && (
              <>
                <Link
                  to={`/lexistar/reports/trip/${trip.id}`}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  <FaFileAlt size={11} /> <span className="hidden sm:inline">Report</span>
                </Link>
                <ExportButtons
                  reportKey="trip_report"
                  parameters={{ trip_id: trip.id }}
                  formats={['pdf']}
                  filenameBase={trip.reference ? `trip-report-${trip.reference}` : undefined}
                  buttonClassName="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                />
              </>
            )}
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
              aria-label="Copy link"
            >
              <FaLink size={11} />
            </button>
            <button
              onClick={() => load()}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
              aria-label="Refresh"
            >
              <FaSyncAlt size={11} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        ) : null
      }
    />
  );

  if (!canView) {
    return <NoPermissionPage header={header} message="You do not have permission to view trips." />;
  }

  const amendedNote =
    trip?.record_state === 'AMENDED' ? (
      <div
        role="note"
        aria-label="Amended watermark"
        className="mb-6 px-4 py-3 rounded-lg border-2 border-slate-800 bg-surface text-slate-900"
      >
        <p className="text-sm font-bold uppercase tracking-wider">
          {trip.watermark || `Amended ${fmtDateTime(trip.amended_at)}`}
        </p>
        <p className="text-sm text-slate-600">
          This trip was changed after it was signed off. Every report, statement and export that
          includes it carries this watermark.
        </p>
      </div>
    ) : null;

  const dialogs = trip ? (
    <>
      <ReasonDialog
        open={dialog === 'cancel'}
        title={`Cancel ${trip.reference || 'trip'}`}
        confirmLabel="Cancel trip"
        busy={busy}
        error={actionError}
        phone={phone}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) => runAction(() => cancelTrip(trip.id, reason), `Cancelled ${trip.reference}.`)}
      />
      <ReplanDialog
        open={dialog === 'replan'}
        trip={trip}
        legs={legs}
        onClose={() => setDialog(null)}
        onDone={async ({ planVersion, budget }: { planVersion?: number | null; budget?: number | null }) => {
          setDialog(null);
          setNotice(
            `Re-planned ${trip.reference}${planVersion ? ` — plan version ${planVersion}` : ''}${budget != null ? ` · allowance ${fmtL(budget)}` : ''}.`
          );
          await load({ quiet: true });
        }}
      />
      <ReasonDialog
        open={dialog === 'abort'}
        title={`Abort ${trip.reference || 'trip'}`}
        confirmLabel="Abort trip"
        busy={busy}
        error={actionError}
        phone={phone}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) => runAction(() => abortTrip(trip.id, reason), `Aborted ${trip.reference}.`)}
      />
    </>
  ) : null;

  if (phone) {
    const stage = String(trip?.stage);
    const actions: ReactNode[] = [];
    if (trip) {
      if (trip.stage === 'PLANNED' && canDispatch)
        actions.push(
          <PhoneAction
            key="dispatch"
            variant="primary"
            icon={FaPaperPlane}
            disabled={busy}
            onClick={() => runAction(() => dispatchTrip(trip.id), `Dispatched ${trip.reference}.`)}
          >
            Dispatch
          </PhoneAction>
        );
      if (ACTIVE_STAGES.includes(stage) && canDispatch && (trip.record_state || 'OPEN') === 'OPEN')
        actions.push(
          <PhoneAction
            key="replan"
            icon={FaDirections}
            onClick={() => {
              setActionError(null);
              setDialog('replan');
            }}
          >
            Re-plan…
          </PhoneAction>
        );
      if (canReplay && trip.stage !== 'PLANNED')
        actions.push(
          <Link key="replay" to={`/lexistar/replay?trip_id=${trip.id}`} className={phoneActionClass()}>
            <FaPlay size={12} aria-hidden="true" />
            <span>Replay</span>
          </Link>
        );
      if (CANCELLABLE.includes(stage) && canManage)
        actions.push(
          <PhoneAction
            key="cancel"
            variant="danger"
            icon={FaBan}
            onClick={() => {
              setActionError(null);
              setDialog('cancel');
            }}
          >
            Cancel trip
          </PhoneAction>
        );
      if (ABORTABLE.includes(stage) && canManage)
        actions.push(
          <PhoneAction
            key="abort"
            variant="danger"
            icon={FaStopCircle}
            onClick={() => {
              setActionError(null);
              setDialog('abort');
            }}
          >
            Abort trip
          </PhoneAction>
        );
    }
    return (
      <div className="min-h-screen flex flex-col bg-slate-50" data-phone-layout="">
        {header}
        <div className="flex-1 py-4 space-y-4">
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
          <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
          {amendedNote}
          {loading && !trip ? (
            <LoadingState label="Loading the trip..." />
          ) : !trip || !data ? null : (
            <>
              <section className={`${cardClasses} p-4 space-y-3`} aria-label="Trip summary">
                <div>
                  <p className="text-base font-semibold text-slate-900 font-mono break-words">{vehicle}</p>
                  <p className="text-sm text-slate-600">
                    {trip.driver_name || trip.driver_tag || 'No driver'}
                    {loadedLeg && ` · ${loadedLeg.from_name} → ${loadedLeg.to_name}`}
                    {loadedLeg?.route_version != null && ` (v${loadedLeg.route_version})`}
                  </p>
                </div>
                <div className="text-sm text-slate-700 space-y-0.5">
                  <p>
                    <span className="font-semibold text-slate-900">
                      {[trip.load?.material, trip.load?.order_ref].filter(Boolean).join(' · ') || 'No load details'}
                    </span>
                    {trip.load?.consignor && ` · Consignor: ${trip.load.consignor}`}
                    {trip.load?.consignee && ` · Consignee: ${trip.load.consignee}`}
                    {trip.load?.planned_tonnes != null && ` · ${fmtNum(trip.load.planned_tonnes, 1)} t planned`}
                  </p>
                  <p className="text-slate-500">
                    Planned start {fmtDateTime(trip.planned_start)}
                    {trip.run_id && trip.seq != null && ` · trip ${trip.seq} of its run`}
                    {trip.origin === 'prompted' && ' · created retrospectively from a detected load'}
                    {trip.eta && ACTIVE_STAGES.includes(stage) && ` · ETA ${fmtTime(trip.eta)}`}
                  </p>
                </div>
                <div className="phone-tools" role="group" aria-label="Trip tools">
                  {canReport && (
                    <>
                      <Link to={`/lexistar/reports/trip/${trip.id}`} className={phoneToolClass}>
                        <FaFileAlt size={12} aria-hidden="true" />
                        <span>Report</span>
                      </Link>
                      <ExportButtons
                        reportKey="trip_report"
                        parameters={{ trip_id: trip.id }}
                        formats={['pdf']}
                        filenameBase={trip.reference ? `trip-report-${trip.reference}` : undefined}
                        buttonClassName={phoneToolClass}
                      />
                    </>
                  )}
                  <button type="button" onClick={shareLink} className={phoneToolClass} aria-label="Share the link">
                    <FaShareAlt size={12} aria-hidden="true" />
                    <span>Share</span>
                  </button>
                  <Link to="/lexistar/status-board" className={phoneToolClass}>
                    <FaArrowLeft size={12} aria-hidden="true" />
                    <span>Board</span>
                  </Link>
                </div>
              </section>

              <Exceptions trip={trip} legs={legs} deviations={deviations} matches={matches} />

              <PhoneMapPanel
                title="Map"
                subject={trip.reference}
                className={`${cardClasses} p-4`}
                footer={!data.track ? 'No actual track has been received for this trip yet.' : undefined}
              >
                <CorridorMap
                  idPrefix="trip"
                  routes={lines}
                  track={data.track}
                  deviations={deviations
                    .filter((d) => d.track)
                    .map((d) => ({ id: d.id, geometry: d.track, approved: d.state === 'APPROVED' }))}
                  positionColor={TONES.loaded.hex}
                  className={PHONE_MAP_CANVAS}
                />
              </PhoneMapPanel>

              <Weights trip={trip} matches={matches} />
              <DistanceTime trip={trip} legs={legs} />
              <Fuel trip={trip} legs={legs} fuel={data.fuel} canSeeRand={canSeeRand} />

              <div className="phone-targets">
                <RecordPanel
                  trip={trip}
                  amendmentsFromTrip={Array.isArray(data.amendments) ? data.amendments : null}
                  refreshKey={data}
                  onChanged={() => load({ quiet: true })}
                  onNotice={setNotice}
                />
              </div>

              <PhoneTimeline timeline={timeline} />
            </>
          )}
        </div>
        {actions.length > 0 && <PhoneActionBar label="Trip actions">{actions}</PhoneActionBar>}
        <div className="phone-targets">{dialogs}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-6">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
        {trip?.record_state === 'AMENDED' && (
          <div
            role="note"
            aria-label="Amended watermark"
            className="mb-6 px-4 py-3 rounded-lg border-2 border-slate-800 bg-surface text-slate-900"
          >
            <p className="text-sm font-bold uppercase tracking-wider">
              {trip.watermark || `Amended ${fmtDateTime(trip.amended_at)}`}
            </p>
            <p className="text-sm text-slate-600">
              This trip was changed after it was signed off. Every report, statement and export that
              includes it carries this watermark.
            </p>
          </div>
        )}

        {loading && !trip ? (
          <LoadingState label="Loading the trip..." />
        ) : !trip || !data ? null : (
          <>
            <div
              className={`${cardClasses} px-5 py-4 mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3`}
            >
              <div className="text-sm text-slate-700 space-y-0.5">
                <p>
                  <span className="font-semibold text-slate-900">
                    {[trip.load?.material, trip.load?.order_ref].filter(Boolean).join(' · ') ||
                      'No load details'}
                  </span>
                  {trip.load?.consignor && ` · Consignor: ${trip.load.consignor}`}
                  {trip.load?.consignee && ` · Consignee: ${trip.load.consignee}`}
                  {trip.load?.planned_tonnes != null &&
                    ` · ${fmtNum(trip.load.planned_tonnes, 1)} t planned`}
                </p>
                <p className="text-slate-500">
                  Planned start {fmtDateTime(trip.planned_start)}
                  {trip.run_id && trip.seq != null && ` · trip ${trip.seq} of its run`}
                  {trip.origin === 'prompted' && ' · created retrospectively from a detected load'}
                  {trip.eta && ACTIVE_STAGES.includes(String(trip.stage)) && ` · ETA ${fmtTime(trip.eta)}`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {trip.stage === 'PLANNED' && canDispatch && (
                  <button
                    onClick={() =>
                      runAction(() => dispatchTrip(trip.id), `Dispatched ${trip.reference}.`)
                    }
                    disabled={busy}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm"
                  >
                    <FaPaperPlane size={12} /> Dispatch
                  </button>
                )}
                {ACTIVE_STAGES.includes(String(trip.stage)) &&
                  canDispatch &&
                  (trip.record_state || 'OPEN') === 'OPEN' && (
                    <button
                      onClick={() => {
                        setActionError(null);
                        setDialog('replan');
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
                    >
                      <FaDirections size={12} /> Re-plan…
                    </button>
                  )}
                {canReplay && trip.stage !== 'PLANNED' && (
                  <Link
                    to={`/lexistar/replay?trip_id=${trip.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    <FaPlay size={10} /> Replay
                  </Link>
                )}
                {CANCELLABLE.includes(String(trip.stage)) && canManage && (
                  <button
                    onClick={() => {
                      setActionError(null);
                      setDialog('cancel');
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    <FaBan size={12} /> Cancel trip
                  </button>
                )}
                {ABORTABLE.includes(String(trip.stage)) && canManage && (
                  <button
                    onClick={() => {
                      setActionError(null);
                      setDialog('abort');
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    <FaStopCircle size={12} /> Abort trip
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Section title="Map" className="xl:row-span-2">
                <CorridorMap
                  idPrefix="trip"
                  routes={lines}
                  track={data.track}
                  deviations={deviations
                    .filter((d) => d.track)
                    .map((d) => ({
                      id: d.id,
                      geometry: d.track,
                      approved: d.state === 'APPROVED',
                    }))}
                  positionColor={TONES.loaded.hex}
                  className="relative z-0 h-[420px] rounded-lg overflow-hidden border border-slate-200"
                />
                {!data.track && (
                  <p className="mt-2 text-xs text-slate-500">
                    No actual track has been received for this trip yet.
                  </p>
                )}
              </Section>
              <Weights trip={trip} matches={matches} />
              <DistanceTime trip={trip} legs={legs} />
              <Fuel trip={trip} legs={legs} fuel={data.fuel} canSeeRand={canSeeRand} />
              <Exceptions trip={trip} legs={legs} deviations={deviations} matches={matches} />
            </div>

            <div className="mt-6">
              <RecordPanel
                trip={trip}
                amendmentsFromTrip={Array.isArray(data.amendments) ? data.amendments : null}
                refreshKey={data}
                onChanged={() => load({ quiet: true })}
                onNotice={setNotice}
              />
            </div>

            <Section title="Timeline" className="mt-6">
              {timeline.length === 0 ? (
                <p className="text-sm text-slate-500">Nothing has happened on this trip yet.</p>
              ) : (
                <ol
                  className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm"
                  aria-label="Trip timeline"
                >
                  {[...timeline]
                    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
                    .map((t, i) => (
                      <li key={t.id ?? i} className="inline-flex items-center gap-2">
                        {i > 0 && (
                          <span className="text-slate-300" aria-hidden="true">
                            →
                          </span>
                        )}
                        <span className="tabular-nums text-slate-500">{fmtTime(t.at)}</span>
                        <span
                          className="text-slate-900"
                          title={t.actor ? `by ${t.actor}` : undefined}
                        >
                          {timelineLabel(t.kind)}
                        </span>
                        {recordedLateAt(t) && (
                          <span
                            className="text-xs text-slate-500"
                            title="Recorded when the truck's tracker next reported"
                          >
                            (recorded {fmtTime(recordedLateAt(t) as string)})
                          </span>
                        )}
                      </li>
                    ))}
                </ol>
              )}
            </Section>
          </>
        )}
      </div>

      <ReasonDialog
        open={dialog === 'cancel'}
        title={`Cancel ${trip?.reference || 'trip'}`}
        confirmLabel="Cancel trip"
        busy={busy}
        error={actionError}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) =>
          trip && runAction(() => cancelTrip(trip.id, reason), `Cancelled ${trip.reference}.`)
        }
      />
      {trip && (
        <ReplanDialog
          open={dialog === 'replan'}
          trip={trip}
          legs={legs}
          onClose={() => setDialog(null)}
          onDone={async ({ planVersion, budget }: { planVersion?: number | null; budget?: number | null }) => {
            setDialog(null);
            setNotice(
              `Re-planned ${trip.reference}${planVersion ? ` — plan version ${planVersion}` : ''}${budget != null ? ` · allowance ${fmtL(budget)}` : ''}.`
            );
            await load({ quiet: true });
          }}
        />
      )}
      <ReasonDialog
        open={dialog === 'abort'}
        title={`Abort ${trip?.reference || 'trip'}`}
        confirmLabel="Abort trip"
        busy={busy}
        error={actionError}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) =>
          trip && runAction(() => abortTrip(trip.id, reason), `Aborted ${trip.reference}.`)
        }
      />
    </div>
  );
};

export default TripDetail;
